import type { ReactNode } from "react";
import { useState } from "react";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import UpdatedBillOfLadingAnimation from "@/Shared/Icons/UpdatedBillOfLadingAnimation";
import { ManageContactsDialogFlavour } from "@/Shared/Contacts/ManageContactsDialogFlavour";
import { ContactsManageDialog } from "@/Shared/Contacts/Dialogs/ContactsManageDialog";
import { locationsFindSavedLocation } from "@/Shared/Locations/Helpers/locationsFindSavedLocation";
import { useLocationsSaved } from "@/Shared/Locations/Hooks/useLocationsSaved";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { useShipmentService } from "@/Services/ShipmentService";
import { useLocationsCreateSavedLocationFromShipment } from "@/Shared/Locations/Hooks/useLocationsCreateSavedLocationFromShipment";
import type { UUID } from "@/Shared/types";

function findSavedLocationId(
  locationContext: LocationContext,
  shipment: Shipment,
  savedLocations: Array<SavedLocation>,
) {
  const location =
    locationContext === LocationContext.Pickup
      ? shipment.pickupLocation
      : shipment.deliveryLocation;

  return locationsFindSavedLocation(savedLocations, location)?.savedLocationId;
}

/**
 * After-booking contact change: opens the manage-contacts dialog, saves the
 * change to the shipment, and (before pickup) shows the updated-BOL notice.
 * Declarative — render the returned `dialogs`; `onChanged` fires when the
 * shipment actually changed so the caller can reload.
 */
function ViewShipmentsModifyContactAfterBookingDialog(
  shipment: Shipment,
  locationContext: LocationContext,
  onChanged: () => void,
): { show: () => Promise<void>; dialogs: ReactNode } {
  const shipmentService = useShipmentService();
  const { savedLocations } = useLocationsSaved();
  const createSavedLocation = useLocationsCreateSavedLocationFromShipment();

  const [savedLocationId, setSavedLocationId] = useState<UUID | undefined>();
  const [manageOpen, setManageOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bolNoticeOpen, setBolNoticeOpen] = useState(false);
  const [changed, setChanged] = useState(false);

  const originalLocation =
    locationContext === LocationContext.Pickup
      ? shipment.pickupLocation
      : shipment.deliveryLocation;

  const originalContact =
    locationContext === LocationContext.Pickup
      ? shipment.pickupContact
      : shipment.deliveryContact;

  async function show() {
    let locationId = findSavedLocationId(
      locationContext,
      shipment,
      savedLocations,
    );

    if (locationId === undefined) {
      locationId = await createSavedLocation(originalLocation, originalContact);
    }

    setSavedLocationId(locationId);
    setChanged(false);
    setManageOpen(true);
  }

  function areContactsEqual(contact1: Contact, contact2: Contact) {
    // Note the email address is missing here. We don't put the
    // contact's email on BOLs or send to the carrier.
    // So if that's all that has changed
    return (
      contact1.contactName === contact2.contactName &&
      contact1.emailAddress === contact2.emailAddress &&
      contact1.phoneNumber === contact2.phoneNumber &&
      contact1.phoneNumberExtension === contact2.phoneNumberExtension
    );
  }

  async function onDone(updatedContact: Contact) {
    setManageOpen(false);

    if (areContactsEqual(updatedContact, originalContact)) {
      // Nothing changed
      return;
    }

    setSaving(true);
    await shipmentService.updateContact(
      shipment.shipmentId,
      locationContext,
      updatedContact,
    );
    setSaving(false);
    setChanged(true);

    // If the shipment is in transit, then it's too late to give the driver
    // a new BOL. But if it's still showing as pending pickup, it's worth
    // showing them this screen.
    if (shipment.shipmentState !== ShipmentState.InTransit) {
      setBolNoticeOpen(true);
    } else {
      onChanged();
    }
  }

  function dismissBolNotice() {
    setBolNoticeOpen(false);
    if (changed) {
      onChanged();
    }
  }

  const dialogs: ReactNode = (
    <>
      <ContactsManageDialog
        open={manageOpen}
        onOpenChange={setManageOpen}
        savedLocationId={savedLocationId}
        flavour={ManageContactsDialogFlavour.ModifyAfterBooking}
        locationContext={locationContext}
        onDone={onDone}
      />
      {/* Saving overlay — replaces the legacy modalsSlice loading backdrop. */}
      <Dialog open={saving} hideCloseButton width="auto">
        <ControlPresentationProvider presentation="underlined">
          <Spinner size={32} />
        </ControlPresentationProvider>
      </Dialog>
      <Dialog
        open={bolNoticeOpen}
        onOpenChange={(open) => {
          if (!open) dismissBolNotice();
        }}
        title="Updated Shipment Documents"
        width="narrow"
        footer={
          <Button primary onClick={dismissBolNotice}>
            Done
          </Button>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          <Stack gap={4}>
            <T size="sm" color="secondary">
              Details of the shipment have been updated, and a new bill of
              lading has been generated, and emailed to the shipper.
            </T>
            <Row justify="center" grow>
              <UpdatedBillOfLadingAnimation />
            </Row>
            <T size="sm" color="secondary">
              To avoid delays in shipment, please make sure the new bill of
              lading is given to the driver for pickup
            </T>
          </Stack>
        </ControlPresentationProvider>
      </Dialog>
    </>
  );

  return { show, dialogs };
}

export default ViewShipmentsModifyContactAfterBookingDialog;
